<script setup>
import { storeToRefs } from 'pinia'
import { useMediaQuery } from '@vueuse/core'
import SocialInstagram from 'assets/icons/social-instagram.svg'
import SocialEmail from 'assets/icons/social-email.svg'
import SocialPhone from 'assets/icons/social-phone.svg'
import SocialWebsite from 'assets/icons/social-website.svg'
import PaperClip from 'assets/icons/paperclip.svg'
import Clock from 'assets/icons/clock.svg'
import { computed } from 'vue'
import { useMessageStore } from '~/stores/messages'

// Define props
const props = defineProps({
    shopSummary: {
        type: Object,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                name: rawValue => rawValue.name === undefined || typeof rawValue.name === 'string',
                contacts: rawValue => rawValue.contacts === undefined || (typeof rawValue.contacts === 'object' && Object.values(rawValue.contacts).every(item => typeof item === 'string')),
                hours: rawValue => rawValue.hours === undefined || rawValue.hours.weekly === undefined ||
                    (Array.isArray(rawValue.hours.weekly) && rawValue.hours.weekly.every((item) => {
                        const isValidDayOfWeek = item.dayOfWeek === undefined || typeof item.dayOfWeek === 'number'
                        const isValidIsClosed = item.isClosed === undefined || typeof item.isClosed === 'boolean'
                        const isValidStartTime = item.operation?.from === undefined || typeof item.operation?.from === 'string'
                        const isValidEndTime = item.operation?.till === undefined || typeof item.operation?.till === 'string'
                        return isValidDayOfWeek && isValidIsClosed && isValidStartTime && isValidEndTime
                    }))
            }

            return Object.entries(validations).every(([_, validate]) => validate(rawValue))
        }
    }
})

// Fetch route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Get center details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

onServerPrefetch(async () => {
    await centerDetailSuspense()
})

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Define breakpoint
const isMobile = useMediaQuery('(max-width: 767px)')

// Get email link
const getEmailLink = computed(() => getFormattedEmailLink(props.shopSummary?.contacts?.email))

// Get phone link
const getPhoneLink = computed(() => getFormattedPhoneLink(props.shopSummary?.contacts?.phone))

// Get instagram link
const getInstagramLink = computed(() => getFormattedWebsiteLink(props.shopSummary?.contacts?.instagram))

// Get website link
const getWebsiteLink = computed(() => getFormattedWebsiteLink(props.shopSummary?.contacts?.website))

// Shop has opening hours
const hasOpeningHours = computed(() => props.shopSummary?.hours?.weekly?.length > 0)

// Shop contacts
const getShopContacts = computed(() => props.shopSummary?.contacts)
const hasShopContacts = computed(() => getEmailLink.value || getPhoneLink.value || getShopContacts.value)
const contactCount = computed(() => props.shopSummary?.contacts ? Object.entries(props.shopSummary?.contacts).length : 0)

const getShopSummaryVisibility = computed(() => {
    if (hasOpeningHours.value && hasShopContacts.value) {
        return true
    }

    if (!hasOpeningHours.value && hasShopContacts.value && !isMobile.value) {
        return true
    }

    return hasOpeningHours.value && !hasShopContacts.value
})

const trackSocialClicks = (type) => {
    // Component is also used in content pages - do not allow tracking here
    if (!props?.shopSummary?.allowTracking) {
        return
    }

    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.SHOP_CONTACT_CLICK,
        shopName: props.shopSummary?.name,
        centerUid: centerDetails.value?.centerUid,
        contactType: type
    })
}

const shopContactConfig = computed(() => {
    return {
        contactCount: contactCount.value,
        items: [
            {
                headline: staticKeys.value?.shop_contact_label_phone,
                render: getPhoneLink.value,
                icon: SocialPhone,
                fn: () => trackSocialClicks('phone')
            },
            {
                headline: staticKeys.value?.shop_contact_label_email,
                render: getEmailLink.value,
                icon: SocialEmail,
                fn: () => trackSocialClicks('email')
            },
            {
                headline: staticKeys.value?.shop_contact_label_website,
                render: getWebsiteLink.value,
                icon: SocialWebsite,
                fn: () => trackSocialClicks('website')
            },
            {
                headline: staticKeys.value?.shop_contact_label_instagram,
                render: getInstagramLink.value,
                icon: SocialInstagram,
                fn: () => trackSocialClicks('instagram')
            }
        ]
    }
})

const shopContactsHeadlineConfig = computed(() => {
    return {
        headline: {
            title: staticKeys.value?.shop_contact_headline,
            type: 'h2'
        },
        icon: {
            name: PaperClip
        }
    }
})

const shopOpeningHoursHeadlineConfig = computed(() => {
    return {
        headline: {
            title: staticKeys.value?.shops_details_openinghours_headline,
            type: 'h2'
        },
        icon: {
            name: Clock
        }
    }
})
</script>

<template>
    <section v-if="getShopSummaryVisibility" class="shop-summary-wrapper">
        <ContentHeadline :content-headline-config="{ title: staticKeys?.shops_details_contact_headline_web_markdow, renderSplit: true }" />

        <div class="shop-summary-container" :class="hasShopContacts ? 'contains-contact-info': ''">
            <DefaultContentBox v-if="hasOpeningHours" class="shop-opening-hours-container">
                <DefaultContentBoxHeadline :content-box-headline-config="shopOpeningHoursHeadlineConfig" />

                <OpeningHours :opening-hours="shopSummary?.hours?.weekly" />
            </DefaultContentBox>

            <DefaultContentBox v-if="hasShopContacts && ! isMobile" class="shop-contact-container">
                <DefaultContentBoxHeadline :content-box-headline-config="shopContactsHeadlineConfig" />

                <table class="shop-contact-table">
                    <tbody>
                        <template v-for="(item, index) in shopContactConfig.items" :key="`shop-content${index}`">
                            <tr v-if="item.render">
                                <td>{{ item.headline }}:</td>
                                <td>
                                    <a
                                        :href="item.render.link"
                                        target="_blank"
                                        :title="item.render.label"
                                        rel="noopener noreferrer"
                                        @click="item.fn"
                                    >
                                        {{ item.render.label }}
                                    </a>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </DefaultContentBox>
        </div>
    </section>

    <section v-if="hasShopContacts && isMobile" class="shop-contact-wrapper">
        <ContentHeadline :content-headline-config="{ title: staticKeys?.center_contact_headline, renderSplit: true }" />

        <SocialContactBar :social-contact-bar-config="shopContactConfig" />
    </section>
</template>

<style lang="scss">
.shop-summary-wrapper {
    margin: 0 0 20px;

    @include bp(medium1) {
        margin: 0 auto 40px;
    }

    @include bp(large2) {
        margin: 0 auto 60px;
    }
}

.shop-summary-container {
    display: flex;
    justify-content: space-between;
    max-width: $component-width;
    margin: 0 auto;
}

.shop-opening-hours-container {
    width: 100%;
    margin: 0 auto;
}

.shop-contact-container {
    margin: 0 auto 20px;
    display: none;

    @include bp(medium1) {
        margin: 0 auto 40px;
        display: block;
    }

    @include bp(large2) {
        margin: 0 auto 60px;
    }
}

.shop-opening-hours-container,
.shop-contact-container {
    @include bp(medium1) {
        margin: 0;
        height: 100%;
    }

    .contains-contact-info & {
        @include bp(medium1) {
            width: calc(50% - 20px);
            display: block;
            margin: 0;
            height: auto;
        }
    }
}

.shop-contact-table {
    width: 100%;

    @include body3-medium;

    @include bp(medium1) {
        @include body2-medium;
    }

    @include bp(large1) {
        @include h4-medium;
    }

    @include bp(large2) {
        margin: 0 0 0 40px;
        width: calc(100% - 40px);
    }

    td {
        padding: 0 0 15px;

        &:first-child {
            padding: 0 20px 15px 0;
            white-space: nowrap;
            width: 1px;

            @include bp(large2) {
                padding: 0 60px 15px 0;
            }
        }

        &:last-child {
            width: 100%;
        }
    }

    tr {
        &:last-child {
            td {
                padding: 0;

                &:first-child {
                    padding: 0 20px 0 0;

                    @include bp(large2) {
                        padding: 0 60px 0 0;
                    }

                }
            }
        }
    }

    a {
        text-decoration: none;
        color: inherit;
        display: block;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        max-width: calc(46vw - 165px);

        @include bp(large1) {
            max-width: calc(42vw - 290px);
        }

        @include bp(large2) {
            max-width: 180px;
        }

        &:hover {
            text-decoration: underline;
        }
    }
}

// Only mobile
.shop-contact-wrapper {
    margin: 0 auto 20px;

    @include bp(medium1) {
        display: none;
    }
}
</style>
